import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { SignedUrl } from '@/plugins/signedUrl/index.ts';
import {
    isSignedUrlExpired,
    parseExpires,
    SIGNED_URL_SAFETY_MARGIN_SECONDS,
} from '@/plugins/signedUrl/cache.ts';
import type { SignedUrlKind, SignedUrlResolvers } from '@/plugins/signedUrl/resolvers.ts';

export type UseSignedUrlOptions<K extends SignedUrlKind> = {
    kind: K;
    params: () => Parameters<(typeof SignedUrlResolvers)[K]>;
    /**
     * When true (default) the composable fetches the URL eagerly on
     * mount and again whenever the params change. Set to false when
     * the component should fetch lazily (e.g. on click only).
     */
    eager?: boolean;
    /**
     * When true (default) a timer schedules an automatic refresh shortly
     * before the current URL expires so consumers always have a fresh
     * value to render.
     */
    autoRefresh?: boolean;
};

/**
 * Reactive helper that resolves a signed URL for the given kind/params
 * and keeps it fresh as the URL approaches expiry. Designed for Vue
 * 2.7 with the Composition API.
 */
export function useSignedUrl<K extends SignedUrlKind>(options: UseSignedUrlOptions<K>) {
    const url = ref<string | null>(null);
    const isLoading = ref(false);
    const error = ref<unknown>(null);
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let lastVersion = 0;

    const expiresAt = computed(() => parseExpires(url.value));
    const isExpired = computed(() => isSignedUrlExpired(url.value));

    const eager = options.eager !== false;
    const autoRefresh = options.autoRefresh !== false;

    function clearTimer() {
        if (refreshTimer !== null) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }
    }

    function scheduleAutoRefresh() {
        clearTimer();
        if (!autoRefresh) return;
        const exp = expiresAt.value;
        if (!exp) return;
        const msUntilRefresh = Math.max(
            (exp - SIGNED_URL_SAFETY_MARGIN_SECONDS) * 1000 - Date.now() - 1000,
            0
        );
        refreshTimer = setTimeout(() => {
            // Force a fresh fetch through the cache (which already
            // treats the entry as expired given the safety margin).
            refresh({ force: true }).catch(() => {});
        }, msUntilRefresh);
    }

    async function refresh({ force = false } = {}) {
        const version = ++lastVersion;
        const args = options.params();
        if (force) {
            (SignedUrl.invalidate as (kind: K, ...args: unknown[]) => void)(
                options.kind,
                ...(args as unknown[])
            );
        }
        isLoading.value = true;
        error.value = null;
        try {
            const fresh = await (
                SignedUrl.resolve as (kind: K, ...args: unknown[]) => Promise<string>
            )(options.kind, ...(args as unknown[]));
            // If a newer call superseded us, drop the result silently.
            if (version !== lastVersion) return null;
            url.value = fresh;
            scheduleAutoRefresh();
            return fresh;
        } catch (e) {
            if (version === lastVersion) {
                error.value = e;
            }
            throw e;
        } finally {
            if (version === lastVersion) {
                isLoading.value = false;
            }
        }
    }

    /**
     * Returns a guaranteed-fresh URL. Cheap when the current value is
     * still valid, fetches otherwise.
     */
    async function ensure(): Promise<string | null> {
        if (url.value && !isExpired.value) return url.value;
        try {
            return await refresh({ force: false });
        } catch (_) {
            return null;
        }
    }

    if (eager) {
        watch(
            () => options.params(),
            () => {
                refresh().catch(() => {});
            },
            { immediate: true, deep: true }
        );
    }

    onBeforeUnmount(() => {
        clearTimer();
        // Bump version so any in-flight resolve does not mutate state.
        lastVersion++;
    });

    return {
        url,
        isLoading,
        isExpired,
        error,
        refresh,
        ensure,
    };
}
